// ProfessionalsView.jsx — View PURA da /professionals. O nome da tela vem do nicho (vm.labels): "Profissionais",
// "Barbeiros", "Dentistas"… A View nunca escreve "Profissional" por conta própria.
// Contrato: professionals.vm.js (@version 2). Recebe { vm, actions }: sem fetch, sem formatação, sem regra de
// negócio — veredito, pendências, preços (com os sinais prontos), rótulos e links (tel:, wa.me, mailto) chegam prontos.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A PERGUNTA DA TELA: "esta pessoa pode ser agendada — onde, o quê, por quanto, e está viva?"
// É o outro lado de Locais (lá: quem atende em cada lugar; aqui: onde cada pessoa atende). Mesma linguagem.
//
// 1. O VEREDITO (readiness) abre tudo, com os três pesos de Agendas e Locais:
//    · recebe          → CALMO: uma linha verde. Se só parte aparece (status warning), o "mas…" vem em âmbar.
//    · não aparece     → ALTO: faixa de tint no topo, com a pendência e o conserto ali mesmo (issues[].fix → onFix).
//    · pausa do dono   → SERENO: ícone de pausa, texto neutro. Nunca vermelho.
//
// 2. PESSOAS, NÃO REGISTROS: o rosto (foto por cima das iniciais; no onError, as iniciais), o título pequeno antes
//    do nome, a especialidade e o registro como uma ficha. Só com o nome (o caso real de 4 em 5) a ficha continua
//    inteira: rosto, nome e "na equipe desde".
//
// 3. UMA PESSOA SÓ (858 de 1.006 contas) chega ABERTA, sem "voltar" (não há lista para onde voltar). A EQUIPE traz a
//    faixa de resumo (a régua de Locais: um traço por pessoa; quem não aparece é mais alto e hachurado), os filtros e
//    os grupos "Sua equipe" / "De outras contas".
//
// 4. A PESSOA ABERTA reflui em colunas: até ~1.000 px tudo empilha (ficha, onde atende, movimento…); a partir daí a
//    ficha fica em cima e "onde atende" divide a largura com o movimento; a partir de ~1.480 px (o monitor de 1920)
//    são TRÊS colunas lado a lado: a ficha, os locais e a atividade.
//    · ONDE ATENDE: cada local é um cartão com o veredito DAQUELE local, a semana de cada agenda (a fita
//      S-T-Q-Q-S-S-D de Agendas) e os serviços. Um local que não recebe ganha a faixa alta; o outro fica calmo —
//      os dois estados se leem num relance.
//    · SERVIÇO QUE NÃO DÁ PARA AGENDAR: o peso vem de notBookableStatus (warning/error = defeito, tingido; neutral =
//      pausa, calmo com o ícone de pausa).
//    · PREÇOS: uma régua por serviço (uma pastilha por forma de pagamento, quebra de linha em vez de rolagem lateral).
//      Igual ao padrão (isDefault) fica quieto, diferente fica forte, "a combinar" (isToBeAgreed) em itálico, e o
//      preço PRÓPRIO (isCustom) ganha a estrela, o fundo da marca e o preço do local embaixo ("No local: R$ 180,00").
//      Nada disso sai do texto do rótulo.
//    · PRÓXIMOS HORÁRIOS (sob demanda): "Ver próximos horários" → onFindNextSlots; buscando / até 3 horários (um toque
//      agenda: onBook com slotKey) / nenhum / falha com "Tentar de novo". O foco não cai na página quando o botão sai.
//    · AGUARDANDO CONFIRMAÇÃO: é ação do dono, não defeito da pessoa — cor de ação (primária suave), no cartão, no
//      topo da pessoa aberta e no Movimento; leva a Agendamentos.
//    · COMPARTILHAMENTO: o mesmo medidor de Gestão de Acessos, nos dois sentidos (quem vê esta pessoa e, nos
//      recebidos, o que dá para fazer). Os NOMES e a ORDEM das áreas vêm de vm.permissionAreas (v2); a View só
//      escolhe o ícone pela key.
//    · RECEBIDO de outra conta: de onde vem, o que dá para fazer (o mesmo medidor de Gestão de Acessos) e, onde o
//      botão faltaria (editar, foto, compartilhar, excluir, preço próprio), o readOnlyReason com um cadeado.
//
// 5. Foco e rolagem (as lições de Acessos e Locais): ao abrir, o foco vai para o nome e o começo da pessoa aparece
//    sem pular a página; ao fechar, o foco volta para quem abriu. O que abre ("Por que isso importa?", os horários)
//    rola só o necessário. 80 px de folga no pé (o aviso do container) em tudo que rola e no fim da página. No
//    primeiro desenho nada rola nem recebe foco.
//
// Texto fixo SEM GÊNERO (não sabemos se é a Ana ou o Rafael, nem se o local é "Unidade" ou "Studio").
// Consistência: CARD_SX, TINT, botões, filtros, faixa de resumo, fita, StateBlock e Movimento iguais a Locais; avatar
// (foto por cima das iniciais) e medidor de níveis iguais a Gestão de Acessos. Neutros de --bg-page/--divider/--text-*.
import { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, TextField, InputAdornment,
  Select, MenuItem, Skeleton, Collapse, CircularProgress,
} from '@mui/material';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SortRounded from '@mui/icons-material/SortRounded';
import FilterAltOffRounded from '@mui/icons-material/FilterAltOffRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PauseCircleRounded from '@mui/icons-material/PauseCircleRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import EventNoteRounded from '@mui/icons-material/EventNoteRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import UpdateRounded from '@mui/icons-material/UpdateRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import LocationOffRounded from '@mui/icons-material/LocationOffRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import MapRounded from '@mui/icons-material/MapRounded';
import CallRounded from '@mui/icons-material/CallRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import PublicRounded from '@mui/icons-material/PublicRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import ShareRounded from '@mui/icons-material/ShareRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import DesignServicesRounded from '@mui/icons-material/DesignServicesRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import PixRounded from '@mui/icons-material/PixRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import HealthAndSafetyRounded from '@mui/icons-material/HealthAndSafetyRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BeachAccessRounded from '@mui/icons-material/BeachAccessRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import FormatQuoteRounded from '@mui/icons-material/FormatQuoteRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (os mesmos papéis de Locais e Agendas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
};
const tint = (tone) => TINT[tone] || TINT.neutral;

// CTA de ativação (a primeira pessoa da equipe), como os vazios de Agendas e Locais.
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';

const HEAD = { fontWeight: 'var(--fw-bold)' };
const NUM = { fontVariantNumeric: 'tabular-nums' };
const ONE_LINE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const BREAK = { overflowWrap: 'anywhere' };
// Em sx, width: 1 é 100% (não 1 px): por isso as medidas vão em string.
const SR_ONLY = { position: 'absolute', width: '1px', height: '1px', m: '-1px', p: 0, border: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
const FOCUS = { '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 } };
const REDUCED = '@media (prefers-reduced-motion: reduce)';
const LIST_RESET = { listStyle: 'none', m: 0, p: 0 };
const OVERLINE = { color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' };

// Folga do aviso do container (Snackbar no pé: 56 px no celular, 72 px a partir de 600 px). 80 px =
// 2 × --space-4 + --space-2. Margem de rolagem de tudo que a View leva à vista, e o fim da página.
const FOLGA = 'calc(var(--space-4) * 2 + var(--space-2))';
const SCROLL_MARGIN = { scrollMarginTop: FOLGA, scrollMarginBottom: FOLGA };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.8 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

const OUTLINE_BTN_SX = {
  minHeight: 40, px: 1.75, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)', borderColor: 'var(--divider)' },
  ...FOCUS,
};

const QUIET_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  ...FOCUS,
};

const LINK_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  ...FOCUS,
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)' },
  ...FOCUS,
};

const DANGER_ICON_BTN_SX = {
  ...ICON_BTN_SX,
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};

// Título que recebe foco por código (tabIndex -1): anel só quando o foco é de teclado.
const FOCUS_TITLE_SX = {
  borderRadius: 'var(--radius-sm)',
  '&:focus': { outline: 'none' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '3px' },
};

const rise = (delay = 0) => ({
  '@keyframes ppRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'ppRise .5s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  [REDUCED]: { animation: 'none' },
});

// Hachura = "não chega ao cliente". Mesmo desenho da régua e da fita de Agendas e Locais.
const hatch = (c) => `repeating-linear-gradient(135deg, ${c} 0 3px, color-mix(in srgb, ${c} 30%, transparent) 3px 6px)`;

const DAY_INITIAL = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const DAY_NAME = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

/* O peso visual de uma pessoa (ou de um local dela) sai SÓ de `readiness`. */
const pesoDe = (r) => (r?.isBookable ? (r.status === 'warning' ? 'partial' : 'live') : r?.isByChoice ? 'waiting' : 'alert');
const tomAlerta = (r) => (r?.status === 'warning' ? 'warning' : 'error');

const PAY_ICON = {
  pix: PixRounded, cash: PaymentsRounded, debitCard: CreditCardRounded, creditCard: CreditCardRounded,
  healthInsurance: HealthAndSafetyRounded, boleto: ReceiptLongRounded, other: SellRounded,
};
const payIcon = (type) => PAY_ICON[type] || SellRounded;

const ISSUE_ICON = { error: ErrorRounded, warning: WarningAmberRounded, info: InfoOutlined, neutral: PauseCircleRounded, success: CheckCircleRounded };
const ISSUE_SR = { error: 'Impede de agendar', warning: 'Atenção', info: 'Vale saber', neutral: 'Escolha sua', success: 'Em ordem' };
const issueTone = (i) => (i?.isByChoice ? 'neutral' : i?.status || 'neutral');
// Títulos fixos de seção (vocabulário da View, sem gênero).
const tituloPendencias = (r) => {
  if (pesoDe(r) === 'alert') return 'O que falta para receber agendamento';
  return (r?.issues || []).some((i) => !i.isByChoice && (i.status === 'error' || i.status === 'warning')) ? 'O que ajustar' : 'Vale saber';
};

// Serviço que não dá para agendar: o PESO vem do status; o ÍCONE diz se é pausa (escolha) ou falta de horário.
const pesoServico = (s) => {
  if (!s || s.isBookable) return null;
  if (s.notBookableIsByChoice) return 'neutral';
  return s.notBookableStatus || 'neutral';
};
const ehDefeito = (tom) => tom === 'warning' || tom === 'error';

// Compartilhamento: o NOME e a ORDEM das áreas vêm de vm.permissionAreas (v2, o vocabulário de Gestão de Acessos);
// aqui fica só o ícone de cada área, pela key — os mesmos de Gestão de Acessos. E os 3 níveis (0 / 1 / 2).
const AREA_ICON = {
  professional: BadgeRounded,
  appointments: EventNoteRounded,
  schedules: CalendarMonthRounded,
  persons: PeopleAltRounded,
};
const LEVELS = [
  { v: 0, label: 'Nenhum', Icon: BlockRounded },
  { v: 1, label: 'Ver', Icon: VisibilityRounded },
  { v: 2, label: 'Editar', Icon: EditRounded },
];
const METER_TONE = {
  0: { bg: 'transparent', border: 'var(--divider)', style: 'dashed', fg: 'var(--text-secondary)', bar: 'var(--divider)' },
  1: { bg: 'var(--bg-page)', border: 'var(--divider)', style: 'solid', fg: 'var(--text-primary)', bar: 'var(--text-secondary)' },
  2: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', style: 'solid', fg: 'var(--tint-success-fg)', bar: 'var(--tint-success-fg)' },
};

/* ── Foco e rolagem (só comportamento de UI) ──────────────────────────── */
const semMovimento = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; }
};
// Rola só o necessário para o elemento aparecer inteiro (block 'nearest' + a folga do scroll-margin).
function mostrar(el) {
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}
// A pessoa aberta é mais alta que a janela, e 'nearest' não rola um elemento que já cobre a tela. Então, ao
// abrir, o que vem à vista é o COMEÇO dela (o veredito vem antes do nome) — só se o começo estiver acima da
// janela ou lá embaixo; se já está no alto da tela, nada se mexe.
function mostrarInicio(el) {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const topo = el.getBoundingClientRect().top;
  const margem = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  if (topo < margem || topo > window.innerHeight / 2) {
    el.scrollIntoView({ block: 'start', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}
// O foco "caiu na página": ninguém focado, ou o elemento focado saiu da tela.
const focoPerdido = () => {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !ae || ae === document.body || ae === document.documentElement || ae.isConnected === false;
};
function levarFoco(el, rolar = true) {
  if (!el || typeof el.focus !== 'function') return;
  el.focus({ preventScroll: true });
  if (rolar) mostrar(el);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

/** O rosto: a foto por cima das iniciais; se a foto falhar (onError), ficam as iniciais. */
function Avatar({ photoUrl, initials, size = 40, ring = false }) {
  const [broken, setBroken] = useState(false);
  const grande = size >= 72;
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative', width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        bgcolor: 'var(--color-primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...(ring && { boxShadow: '0 0 0 2px var(--surface)' }),
        ...(grande && { boxShadow: '0 0 0 4px var(--surface), 0 0 0 5px color-mix(in srgb, var(--color-primary) 30%, transparent)' }),
      }}
    >
      <Typography
        component="span"
        variant={grande ? 'h5' : size >= 48 ? 'subtitle1' : size >= 36 ? 'subtitle2' : 'caption'}
        sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', lineHeight: 1, letterSpacing: '.02em' }}
      >
        {initials}
      </Typography>
      {!!photoUrl && !broken && (
        <Box component="img" src={photoUrl} alt="" loading="lazy" onError={() => setBroken(true)} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </Box>
  );
}

/** O nome como foi cadastrado, com o título pequeno antes quando ele vem à parte (titleLabel). */
function PersonName({ card, variant = 'subtitle1', component = 'h3', id, headRef, focusable = false }) {
  return (
    <Typography
      id={id}
      ref={headRef}
      tabIndex={focusable ? -1 : undefined}
      variant={variant}
      component={component}
      sx={{
        ...HEAD, ...BREAK, color: 'var(--text-primary)', lineHeight: 1.25, textWrap: 'pretty',
        ...(focusable && { ...FOCUS_TITLE_SX, ...SCROLL_MARGIN, width: 'fit-content', maxWidth: '100%' }),
      }}
    >
      {card.titleLabel && (
        <Box component="span" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', mr: 0.5 }}>{card.titleLabel}</Box>
      )}
      {card.name}
    </Typography>
  );
}

/** Linha de contexto: ícone discreto + texto JÁ ESCRITO pelo vm. */
function ContextLine({ icon, children, tone }) {
  const Icon = icon;
  const cor = tone ? tint(tone).fg : 'var(--text-secondary)';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: cor }}>
      <Icon sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }} aria-hidden="true" />
      <Typography variant="caption" sx={{ color: 'inherit', ...BREAK, textWrap: 'pretty' }}>{children}</Typography>
    </Box>
  );
}

/** Cadeado + o motivo, no lugar do botão que falta (recebidos). */
function LockLine({ children }) {
  if (!children) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: 'var(--text-secondary)', minWidth: 0 }}>
      <LockRounded sx={{ fontSize: 16, flexShrink: 0, mt: '2px' }} aria-hidden="true" />
      <Typography variant="caption" sx={{ color: 'inherit', textWrap: 'pretty', ...BREAK }}>{children}</Typography>
    </Box>
  );
}

/** Amostra de estado — o mesmo desenho na régua, nos números e na troca de pessoa. */
function Swatch({ kind, tone = 'error', height = 10, width = 18 }) {
  const t = tint(tone);
  const base = { width, height, borderRadius: 3, flexShrink: 0, boxSizing: 'border-box' };
  if (kind === 'live' || kind === 'partial') return <Box aria-hidden="true" sx={{ ...base, bgcolor: 'var(--color-success)' }} />;
  if (kind === 'waiting') return <Box aria-hidden="true" sx={{ ...base, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }} />;
  return <Box aria-hidden="true" sx={{ ...base, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` }} />;
}

/** Fita S-T-Q-Q-S-S-D, a mesma de Agendas: cheio = gera horário; tracejado = há turno, mas não chega. */
function WeekStrip({ days, mode, tone = 'error' }) {
  const t = tint(mode === 'alert' ? tone : 'neutral');
  return (
    <Box component="ul" aria-label="Dias da semana" sx={{ ...LIST_RESET, display: 'flex', gap: 0.5, maxWidth: 252 }}>
      {DAY_INITIAL.map((d, i) => {
        const on = !!(days && days[i]);
        const state = on ? (mode === 'live' ? 'on' : 'planned') : 'off';
        const srText = state === 'on' ? 'tem horário para o cliente' : state === 'planned' ? 'tem turno, mas não gera horário' : 'sem atendimento';
        return (
          <Box
            component="li"
            key={i}
            aria-label={`${DAY_NAME[i]}: ${srText}`}
            sx={{
              flex: '1 1 0', minWidth: 0, height: 26, ml: i === 5 ? 0.5 : 0, borderRadius: '6px', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...(state === 'on' && { bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' }),
              ...(state === 'planned' && { border: '1.5px dashed', borderColor: t.main, color: t.fg }),
              ...(state === 'off' && { bgcolor: 'var(--bg-page)', color: 'var(--text-disabled)' }),
            }}
          >
            <Typography variant="caption" aria-hidden="true" sx={{ fontWeight: state === 'off' ? 'var(--fw-regular)' : 'var(--fw-bold)' }}>{d}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function EmptyNote({ icon, tone = 'neutral', children, action }) {
  const Icon = icon || InfoOutlined;
  const t = tint(tone);
  const calmo = tone === 'neutral' || tone === 'info';
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, borderRadius: 'var(--radius-sm)',
        border: '1px dashed', borderColor: calmo ? 'var(--divider)' : t.border, bgcolor: calmo ? 'transparent' : t.bg,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: calmo ? 'var(--text-secondary)' : t.fg }}>
        <Icon sx={{ fontSize: 20, flexShrink: 0 }} aria-hidden="true" />
        <Typography variant="body2" sx={{ color: 'inherit', textWrap: 'pretty' }}>{children}</Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

/** "2 aguardando confirmação" — a pendência da Home, por pessoa. É AÇÃO do dono (leva a Agendamentos), não
 *  defeito da pessoa: por isso a cor de ação (primária suave), e não âmbar ou vermelho. */
const aguardando = (act) => (act && act.awaitingConfirmationCount > 0 && act.awaitingConfirmationLabel ? act.awaitingConfirmationLabel : null);

function AwaitingButton({ label, personId, a }) {
  return (
    <ButtonBase
      onClick={() => a.onOpenAwaitingConfirmation?.(personId)}
      sx={{
        alignSelf: 'flex-start', maxWidth: '100%', minHeight: 40, px: 1.25, gap: 0.75, borderRadius: 999,
        bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
        border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
        transition: 'border-color .15s', '&:hover': { borderColor: 'var(--color-primary)' },
        '&:hover .pp-seta': { transform: 'translateX(3px)' }, ...FOCUS,
      }}
    >
      <HourglassTopRounded sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'inherit', textAlign: 'left' }}>
        {label}
        <Box component="span" sx={SR_ONLY}> — confirmar em Agendamentos</Box>
      </Typography>
      <ArrowForwardRounded className="pp-seta" sx={{ fontSize: 16, flexShrink: 0, transition: 'transform .2s', [REDUCED]: { transition: 'none' } }} aria-hidden="true" />
    </ButtonBase>
  );
}

/** O medidor de níveis de Gestão de Acessos (leitura): cada área de vm.permissionAreas, com o nível e 2 barrinhas. */
function PermMeter({ areas, levels, label }) {
  const lv = levels || {};
  const lista = areas || [];
  if (!lista.length) return null;
  return (
    <Box sx={{ containerType: 'inline-size', containerName: 'medidor', minWidth: 0 }}>
      <Box
        component="ul"
        aria-label={label}
        sx={{
          ...LIST_RESET, display: 'grid', gap: 0.75, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          '@container medidor (min-width: 520px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        {lista.map((ar) => {
          const level = lv[ar.key] ?? 0;
          const t = METER_TONE[level] || METER_TONE[0];
          const L = LEVELS[level] || LEVELS[0];
          const LevelIcon = L.Icon;
          const AreaIcon = AREA_ICON[ar.key] || BadgeRounded;
          return (
            <Box
              component="li"
              key={ar.key}
              aria-label={`${ar.label}: ${L.label}`}
              sx={{
                minWidth: 0, bgcolor: t.bg, border: '1px', borderStyle: t.style, borderColor: t.border,
                borderRadius: 'var(--radius-sm)', px: 1.125, py: 0.875, display: 'flex', flexDirection: 'column', gap: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <AreaIcon aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-secondary)', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...ONE_LINE }}>{ar.label}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <LevelIcon aria-hidden="true" sx={{ fontSize: 15, color: t.fg, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: t.fg, fontWeight: level ? 'var(--fw-bold)' : 'var(--fw-medium)', whiteSpace: 'nowrap' }}>{L.label}</Typography>
                </Box>
                <Box aria-hidden="true" sx={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                  {[1, 2].map((i) => <Box key={i} sx={{ width: 10, height: 6, borderRadius: 999, bgcolor: level >= i ? t.bar : 'var(--divider)' }} />)}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VEREDITO
   ═══════════════════════════════════════════════════════════════════════════ */

/** Peso ALTO: faixa de tint no topo do cartão (ou da pessoa, ou do local dela). */
function AlertBand({ r, size = 'md', children }) {
  const t = tint(tomAlerta(r));
  const lg = size === 'lg';
  return (
    <Box sx={{ bgcolor: t.bg, borderBottom: '1px solid', borderColor: t.border, px: { xs: 2, sm: lg ? 3 : 2.25 }, py: lg ? 2 : 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: lg ? 44 : 36, height: lg ? 44 : 36, borderRadius: '50%', flexShrink: 0, bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EventBusyRounded sx={{ fontSize: lg ? 24 : 20, color: t.fg }} aria-hidden="true" />
        </Box>
        <Box sx={{ minWidth: 0, pt: '2px' }}>
          <Typography variant={lg ? 'subtitle1' : 'subtitle2'} component="p" sx={{ ...HEAD, color: t.fg, textWrap: 'pretty' }}>{r.headline}</Typography>
          {r.detail && <Typography variant="body2" sx={{ color: t.fg, textWrap: 'pretty', ...BREAK }}>{r.detail}</Typography>}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

/** Pesos CALMO (recebe — com o "mas…" em âmbar quando só parte aparece) e SERENO (pausa do dono). */
function VerdictLine({ r, size = 'md' }) {
  const peso = pesoDe(r);
  const waiting = peso === 'waiting';
  const t = tint(waiting ? 'neutral' : 'success');
  const Icon = waiting ? PauseCircleRounded : CheckCircleRounded;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.fg }}>
        <Icon sx={{ fontSize: size === 'lg' ? 22 : 18, flexShrink: 0 }} aria-hidden="true" />
        <Typography variant={size === 'lg' ? 'subtitle1' : 'body2'} component="p" sx={{ ...HEAD, color: t.fg }}>{r.headline}</Typography>
      </Box>
      {r.detail && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: peso === 'partial' ? 'var(--tint-warning-fg)' : 'var(--text-secondary)', pl: peso === 'partial' ? 0 : 3.25 }}>
          {peso === 'partial' && <WarningAmberRounded sx={{ fontSize: 17, mt: '1px', flexShrink: 0, mr: 0.5 }} aria-hidden="true" />}
          <Typography variant="body2" component="p" sx={{ color: 'inherit', textWrap: 'pretty', ...BREAK }}>{r.detail}</Typography>
        </Box>
      )}
    </Box>
  );
}

/** Uma pendência, com o porquê sob demanda e o conserto ali mesmo. */
function IssueRow({ issue, personId, scope, a, principal, open, onToggle }) {
  const tone = issueTone(issue);
  const t = tint(tone);
  const Icon = ISSUE_ICON[issue.isByChoice ? 'neutral' : issue.status] || InfoOutlined;
  const whyId = `pp-why-${scope}-${issue.key}`;
  const temConserto = !!(issue.actionLabel && issue.fix);
  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 1.25 }}>
      <Box aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, bgcolor: t.bg, border: '1.5px solid', borderColor: tone === 'neutral' ? 'var(--divider)' : t.border, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px' }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 1.5, rowGap: 0.75 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 200px', pt: '4px' }}>
            <Typography variant="subtitle2" component="p" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              <Box component="span" sx={SR_ONLY}>{`${ISSUE_SR[tone] || ''}: `}</Box>
              {issue.label}
            </Typography>
            {issue.why && (
              <Button
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={whyId}
                endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
                sx={{ ...QUIET_BTN_SX, minHeight: 40, px: 0.75, ml: -0.75, color: tone === 'neutral' ? 'var(--text-secondary)' : t.sub }}
              >
                Por que isso importa?
              </Button>
            )}
          </Box>
          {temConserto && (
            <Button
              onClick={() => a.onFix?.(personId, issue.fix)}
              disableElevation
              endIcon={<ArrowForwardRounded sx={{ fontSize: 18, transition: 'transform .2s' }} />}
              sx={{ ...(principal ? PRIMARY_BTN_SX : OUTLINE_BTN_SX), minHeight: 40, whiteSpace: 'normal', textAlign: 'left', '&:hover .MuiButton-endIcon svg': { transform: 'translateX(3px)' } }}
            >
              {issue.actionLabel}
            </Button>
          )}
        </Box>
        <Collapse in={open} unmountOnExit onEntered={(node) => mostrar(node.querySelector(`[id="${whyId}"]`) || node)}>
          <Box id={whyId} sx={{ mt: 1, p: 1.5, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', ...SCROLL_MARGIN }}>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', textWrap: 'pretty', ...BREAK }}>{issue.why}</Typography>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function IssueList({ issues, personId, scope = 'pessoa', a, title }) {
  const [openWhy, setOpenWhy] = useState(null); // UI: qual "por quê" está aberto
  const list = issues || [];
  if (!list.length) return null;
  // O "próximo passo" (botão cheio) é o primeiro conserto de algo que IMPEDE; os outros, contorno.
  const principal = (list.find((i) => !i.isByChoice && i.status === 'error' && i.fix && i.actionLabel) || {}).key;
  return (
    <Box sx={{ minWidth: 0 }}>
      {title && <Typography variant="overline" component="h3" sx={{ ...OVERLINE, display: 'block' }}>{title}</Typography>}
      <Box component="ul" sx={{ ...LIST_RESET, '& > li + li': { borderTop: '1px solid', borderColor: 'var(--divider)' } }}>
        {list.map((i) => (
          <IssueRow
            key={i.key}
            issue={i}
            personId={personId}
            scope={scope}
            a={a}
            principal={i.key === principal}
            open={openWhy === i.key}
            onToggle={() => setOpenWhy((k) => (k === i.key ? null : i.key))}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O RESUMO DA EQUIPE
   ═══════════════════════════════════════════════════════════════════════════ */

function Roster({ items, onJump }) {
  if (!items || items.length < 2) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 28, minWidth: 0 }}>
      {items.map((it) => {
        const r = it.readiness || {};
        const peso = pesoDe(r);
        const t = tint(tomAlerta(r));
        const bar = peso === 'live' || peso === 'partial'
          ? { height: 8, bgcolor: 'var(--color-success)' }
          : peso === 'waiting'
            ? { height: 8, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }
            : { height: 16, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` };
        return (
          <Tooltip key={it.id} title={`${it.name} — ${r.headline || ''}`}>
            <ButtonBase
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => onJump(it.id)}
              sx={{ flex: '1 1 0', minWidth: 6, maxWidth: 56, height: 28, display: 'flex', alignItems: 'center', '&:hover > span': { transform: 'scaleY(1.35)' } }}
            >
              <Box component="span" sx={{ display: 'block', width: '100%', borderRadius: 3, boxSizing: 'border-box', transition: 'transform .15s', ...bar }} />
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function StatTile({ kind, tone, label, onClick }) {
  const t = tint(kind === 'alert' ? tone : 'success');
  const fg = kind === 'alert' ? t.fg : 'var(--text-primary)';
  const content = (
    <>
      <Swatch kind={kind} tone={tone} width={14} height={kind === 'alert' ? 14 : 8} />
      <Typography variant="body2" component="span" sx={{ ...HEAD, color: kind === 'waiting' ? 'var(--text-secondary)' : fg, textAlign: 'left' }}>{label}</Typography>
      {onClick && <ArrowForwardRounded sx={{ fontSize: 16, color: t.fg }} aria-hidden="true" />}
    </>
  );
  const sx = {
    display: 'inline-flex', alignItems: 'center', gap: 1, minHeight: 44, px: 1.5, py: 0.75,
    borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', border: '1px solid',
    borderColor: kind === 'alert' ? t.border : 'var(--divider)', maxWidth: '100%',
  };
  if (!onClick) return <Box sx={sx}>{content}</Box>;
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${label}. Ir até o primeiro.`}
      sx={{ ...sx, transition: 'border-color .15s, transform .15s', '&:hover': { borderColor: t.main, transform: 'translateY(-1px)' }, ...FOCUS, [REDUCED]: { '&:hover': { transform: 'none' } } }}
    >
      {content}
    </ButtonBase>
  );
}

function SummaryBand({ summary, items, onJump, firstAlert }) {
  const s = summary || {};
  const t = tint(s.status);
  const calm = !['error', 'warning'].includes(s.status);
  const vida = [s.futureAppointmentsLabel, s.nextAppointmentLabel].filter(Boolean).join(' · ');
  return (
    <Box
      component="section"
      aria-label="Situação da equipe"
      sx={{
        ...CARD_SX, ...rise(0),
        bgcolor: calm ? 'var(--surface)' : t.bg, borderColor: calm ? 'var(--divider)' : t.border,
        p: { xs: 2, sm: 2.25 }, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: { md: 'wrap' }, alignItems: { xs: 'stretch', md: 'center' }, columnGap: 2.5, rowGap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <GroupsRounded sx={{ fontSize: 28, color: calm ? 'var(--color-primary-dark)' : t.fg, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="h6" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{s.totalLabel}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {s.bookableLabel && <StatTile kind="live" label={s.bookableLabel} />}
          {s.attentionCount > 0 && s.attentionLabel && (
            <StatTile kind="alert" tone={tomAlerta(firstAlert?.readiness)} label={s.attentionLabel} onClick={firstAlert ? () => onJump(firstAlert.id) : undefined} />
          )}
          {s.byChoiceLabel && <StatTile kind="waiting" label={s.byChoiceLabel} />}
        </Box>
        {/* A régua pega o que sobra da linha; sem espaço para um traço por pessoa, desce para a linha de baixo. */}
        <Box sx={{ flex: '1 1 220px', minWidth: { md: 160 } }}>
          <Roster items={items} onJump={onJump} />
        </Box>
      </Box>
      {(vida || s.absentLabel || s.sharedOutLabel) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.75 }}>
          {vida && <ContextLine icon={EventAvailableRounded}>{vida}</ContextLine>}
          {s.absentLabel && <ContextLine icon={BeachAccessRounded} tone="info">{s.absentLabel}</ContextLine>}
          {s.sharedOutLabel && <ContextLine icon={HandshakeRounded}>{s.sharedOutLabel}</ContextLine>}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Filtros
   ═══════════════════════════════════════════════════════════════════════════ */

const SELECT_SX = { '& .MuiSelect-select': { minHeight: '24px !important', py: '9px', display: 'flex', alignItems: 'center' } };

function FiltersBar({ filters, labels, a }) {
  const f = filters || {};
  const segs = f.segmentOptions || [];
  const result = f.resultLabel ? (
    <Typography variant="caption" role="status" sx={{ color: 'var(--text-secondary)', ...NUM, ml: { sm: 'auto' } }}>{f.resultLabel}</Typography>
  ) : null;
  return (
    <Box sx={{ ...CARD_SX, p: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
        <TextField
          value={f.searchText || ''}
          onChange={(e) => a.onSearchChange?.(e.target.value)}
          placeholder={labels.searchPlaceholder}
          size="small"
          inputProps={{ 'aria-label': labels.searchPlaceholder }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} /></InputAdornment>) }}
          sx={{ flex: '2 1 260px', minWidth: 0, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', minHeight: 42, bgcolor: 'var(--surface)' }, '& .MuiOutlinedInput-input': { minHeight: 24, py: '9px' } }}
        />
        {(f.sortOptions || []).length > 0 && (
          <Select
            value={f.sortOrder || ''}
            onChange={(e) => a.onSortChange?.(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': 'Ordenar' }}
            startAdornment={<InputAdornment position="start"><SortRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} /></InputAdornment>}
            sx={{ flex: '1 1 190px', minWidth: 0, minHeight: 42, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', ...SELECT_SX }}
          >
            {f.sortOptions.map((o) => <MenuItem key={o.value} value={o.value} sx={{ minHeight: 40 }}>{o.label}</MenuItem>)}
          </Select>
        )}
        <Button onClick={() => a.onClearFilters?.()} disabled={!f.canClear} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>
          Limpar
        </Button>
        {segs.length <= 1 && result}
      </Box>
      {segs.length > 1 && (
        <Box role="group" aria-label="Mostrar" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          {segs.map((o) => {
            const on = f.segment === o.value;
            return (
              <ButtonBase
                key={o.value}
                aria-pressed={on}
                onClick={() => a.onSegmentChange?.(o.value)}
                sx={{
                  minHeight: 40, px: 1.5, gap: 0.75, borderRadius: 999, border: '1px solid', maxWidth: '100%',
                  borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
                  bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
                  color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)',
                  transition: 'border-color .15s, background-color .15s',
                  '&:hover': { borderColor: 'var(--color-primary)' },
                  ...FOCUS,
                }}
              >
                {on && <CheckRounded sx={{ fontSize: 16 }} aria-hidden="true" />}
                <Typography variant="body2" component="span" sx={{ fontWeight: on ? 'var(--fw-bold)' : 'var(--fw-medium)', color: 'inherit', textAlign: 'left' }}>{o.label}</Typography>
                <Box component="span" sx={{ minWidth: 22, height: 22, px: 0.75, borderRadius: 999, bgcolor: on ? 'var(--surface)' : 'var(--bg-page)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" component="span" sx={{ ...HEAD, ...NUM, color: 'inherit' }}>{o.count}</Typography>
                </Box>
              </ButtonBase>
            );
          })}
          {result}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A EQUIPE (lista)
   ═══════════════════════════════════════════════════════════════════════════ */

const vidaDe = (act) => {
  const x = act || {};
  if (x.futureLabel) return [x.futureLabel, x.nextAppointmentLabel].filter(Boolean).join(' · ');
  return x.lastAppointmentLabel || x.emptyLabel || null;
};

function AbsenceLine({ absence }) {
  if (!absence) return null;
  return (
    <ContextLine icon={BeachAccessRounded} tone={absence.isNow || absence.status === 'warning' ? 'warning' : 'info'}>
      {[absence.label, absence.reason, absence.placesLabel].filter(Boolean).join(' · ')}
    </ContextLine>
  );
}

/** De qual conta vem (recebidos): o dono, o que dá para fazer e o porquê do botão que falta. */
function ReceivedTag({ own }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--tint-info-fg)', minWidth: 0 }}>
        <HandshakeRounded sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
        <Typography variant="body2" component="p" sx={{ ...HEAD, color: 'inherit', ...BREAK }}>
          <Box component="span" sx={SR_ONLY}>Conta de origem: </Box>
          {own.ownerName}
        </Typography>
      </Box>
      {own.accessLabel && <Typography variant="caption" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>{own.accessLabel}</Typography>}
      {own.readOnlyReason && <LockLine>{own.readOnlyReason}</LockLine>}
    </Box>
  );
}

function ProfessionalCard({ item, a, cardRef, openRef, flash, delay }) {
  const r = item.readiness || {};
  const peso = pesoDe(r);
  const t = tint(tomAlerta(r));
  const own = item.ownership || {};
  const headingId = `pp-${item.id}`;
  const issues = r.issues || [];
  const fortes = issues.filter((i) => !i.isByChoice && i.status !== 'info');
  const primeira = fortes.find((i) => i.fix && i.actionLabel);
  const places = item.placesSummary || {};
  const services = item.servicesSummary || {};
  const vida = vidaDe(item.activity);
  const espera = aguardando(item.activity);
  const pres = item.presence || {};
  const recebido = own.kind === 'received';
  const podeAgendar = own.canBook && r.isBookable;
  return (
    <Box
      component="article"
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={{
        ...CARD_SX, ...rise(delay), overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
        outline: 'none', ...SCROLL_MARGIN,
        borderColor: peso === 'alert' ? t.border : 'var(--divider)',
        transition: 'box-shadow .2s, border-color .2s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: peso === 'alert' ? t.main : 'var(--color-primary-light)' },
        ...(flash && { boxShadow: '0 0 0 3px var(--color-primary-light), var(--shadow-hover)' }),
      }}
    >
      {peso === 'alert' ? (
        <AlertBand r={r}>
          {fortes.length > 1 && (
            <Box component="ul" aria-label="O que falta" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.5, pl: { xs: 0, sm: 6.5 } }}>
              {fortes.map((i) => (
                <Box component="li" key={i.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: t.fg }}>
                  <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.main, mt: '7px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)', ...BREAK }}>{i.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {primeira && (
            <Box sx={{ pl: { xs: 0, sm: 6.5 } }}>
              <Button
                onClick={() => a.onFix?.(item.id, primeira.fix)}
                disableElevation
                endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
                sx={{ ...PRIMARY_BTN_SX, minHeight: 40, whiteSpace: 'normal', textAlign: 'left' }}
              >
                {primeira.actionLabel}
              </Button>
            </Box>
          )}
        </AlertBand>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 2.25 }, pt: { xs: 2, sm: 2.25 } }}>
          <VerdictLine r={r} />
        </Box>
      )}

      <Box sx={{ p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar photoUrl={item.photoUrl} initials={item.initials} size={56} />
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <PersonName card={item} id={headingId} />
            {item.specialty && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{item.specialty}</Typography>}
            {item.registration && <ContextLine icon={BadgeRounded}>{item.registration}</ContextLine>}
            {!item.specialty && !item.registration && item.sinceLabel && <ContextLine icon={AccessTimeRounded}>{item.sinceLabel}</ContextLine>}
          </Box>
        </Box>

        {recebido && <ReceivedTag own={own} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: places.count ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {places.count ? <PlaceRounded sx={{ fontSize: 18, flexShrink: 0, mt: '1px' }} aria-hidden="true" /> : <LocationOffRounded sx={{ fontSize: 18, flexShrink: 0, mt: '1px' }} aria-hidden="true" />}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)', ...BREAK }}>{places.label}</Typography>
              {places.count > 1 && (places.names || []).length > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', ...BREAK }}>{places.names.join(' · ')}</Typography>
              )}
            </Box>
          </Box>
          {services.label && <ContextLine icon={DesignServicesRounded}>{services.label}</ContextLine>}
        </Box>

        {(vida || espera || pres.label || item.absence || item.sharing?.label) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 'auto' }}>
            {vida && <ContextLine icon={EventAvailableRounded}>{vida}</ContextLine>}
            {espera && <Box sx={{ display: 'flex', py: 0.25 }}><AwaitingButton label={espera} personId={item.id} a={a} /></Box>}
            {pres.label && <ContextLine icon={LanguageRounded}>{pres.label}</ContextLine>}
            {item.sharing?.label && <ContextLine icon={ShareRounded}>{item.sharing.label}</ContextLine>}
            <AbsenceLine absence={item.absence} />
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 0.75, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          ref={openRef}
          onClick={() => a.onOpen?.(item.id)}
          endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
          aria-label={`Ver detalhes de ${item.name}`}
          sx={{ ...LINK_BTN_SX, ...SCROLL_MARGIN }}
        >
          Ver detalhes
        </Button>
        <Box sx={{ flex: 1 }} />
        {podeAgendar && (
          <Tooltip title="Agendar">
            <IconButton aria-label={`Agendar com ${item.name}`} onClick={() => a.onBook?.(item.id)} sx={ICON_BTN_SX}>
              <EventAvailableRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        {own.canEdit && (
          <Tooltip title="Editar">
            <IconButton aria-label={`Editar ${item.name}`} onClick={() => a.onEdit?.(item.id)} sx={ICON_BTN_SX}>
              <EditRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        {own.canDelete && (
          <Tooltip title="Excluir">
            <IconButton aria-label={`Excluir ${item.name}`} onClick={() => a.onDelete?.(item.id)} sx={DANGER_ICON_BTN_SX}>
              <DeleteOutlineRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

const CARDS_GRID = {
  display: 'grid', gap: 'var(--space-2)', alignItems: 'stretch',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
};

function GroupSection({ group, showHeader, a, regCard, regOpen, flash, offset }) {
  const items = group.items || [];
  const headingId = `pp-grupo-${group.key}`;
  const recebidos = group.key === 'received';
  return (
    <Box component="section" aria-labelledby={showHeader ? headingId : undefined} aria-label={showHeader ? undefined : group.title} sx={{ mb: 'var(--space-3)' }}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          {recebidos
            ? <HandshakeRounded sx={{ fontSize: 22, color: 'var(--tint-info-fg)', mt: '2px' }} aria-hidden="true" />
            : <GroupsRounded sx={{ fontSize: 22, color: 'var(--color-primary-dark)', mt: '2px' }} aria-hidden="true" />}
          <Box sx={{ minWidth: 0 }}>
            <Typography id={headingId} variant="h6" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{group.title}</Typography>
            {group.description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{group.description}</Typography>}
          </Box>
        </Box>
      )}
      {items.length === 0 ? (
        group.emptyLabel ? <EmptyNote icon={InfoOutlined} tone="info">{group.emptyLabel}</EmptyNote> : null
      ) : (
        <Box sx={CARDS_GRID}>
          {items.map((it, i) => (
            <ProfessionalCard
              key={it.id}
              item={it}
              a={a}
              cardRef={regCard(it.id)}
              openRef={regOpen(it.id)}
              flash={flash === it.id}
              delay={Math.min((offset + i) * 50, 400)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A PESSOA ABERTA
   ═══════════════════════════════════════════════════════════════════════════ */

function Section({ id, icon, title, subtitle, action, container, children, sx }) {
  const Icon = icon;
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        ...CARD_SX, p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0,
        ...(container && { containerType: 'inline-size', containerName: container }),
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: '1 1 200px' }}>
          <Icon sx={{ fontSize: 20, color: 'var(--color-primary-dark)', mt: '2px', flexShrink: 0 }} aria-hidden="true" />
          <Box sx={{ minWidth: 0 }}>
            <Typography id={id} variant="subtitle1" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{title}</Typography>
            {subtitle && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** Voltar e trocar de pessoa sem passar pela lista. */
function DetailNav({ items, selectedId, labels, a }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 'var(--space-2)' }}>
      <Button onClick={() => a.onCloseDetail?.()} startIcon={<ArrowBackRounded />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
        {labels.title}
      </Button>
      <Box sx={{ flex: 1 }} />
      <Box component="nav" aria-label={labels.title} sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end', minWidth: 0 }}>
        {items.map((it) => {
          const atual = it.id === selectedId;
          const peso = pesoDe(it.readiness);
          return (
            <ButtonBase
              key={it.id}
              aria-current={atual ? 'page' : undefined}
              onClick={atual ? undefined : () => a.onOpen?.(it.id)}
              sx={{
                minHeight: 40, maxWidth: 240, pl: 0.5, pr: 1.25, gap: 0.75, borderRadius: 999, border: '1px solid',
                borderColor: atual ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: atual ? 'var(--color-primary-soft)' : 'var(--surface)',
                color: atual ? 'var(--color-primary-dark)' : 'var(--text-primary)',
                cursor: atual ? 'default' : 'pointer', '&:hover': atual ? {} : { borderColor: 'var(--color-primary)' }, ...FOCUS,
              }}
            >
              <Avatar photoUrl={it.photoUrl} initials={it.initials} size={30} />
              <Typography variant="body2" component="span" sx={{ ...ONE_LINE, fontWeight: atual ? 'var(--fw-bold)' : 'var(--fw-medium)', color: 'inherit' }}>{it.name}</Typography>
              <Swatch kind={peso} tone={tomAlerta(it.readiness)} width={10} height={peso === 'alert' ? 12 : 8} />
              <Box component="span" sx={SR_ONLY}>{`— ${it.readiness?.headline || ''}`}</Box>
            </ButtonBase>
          );
        })}
      </Box>
      <Select
        value={selectedId || ''}
        onChange={(e) => { if (e.target.value !== selectedId) a.onOpen?.(e.target.value); }}
        size="small"
        inputProps={{ 'aria-label': labels.title }}
        sx={{ display: { xs: 'flex', sm: 'none' }, flex: '1 1 160px', minWidth: 0, minHeight: 42, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', ...SELECT_SX }}
      >
        {items.map((it) => <MenuItem key={it.id} value={it.id} sx={{ minHeight: 44 }}>{it.name}</MenuItem>)}
      </Select>
    </Box>
  );
}

function ContactLinks({ contact }) {
  const c = contact || {};
  const links = [
    c.phoneHref && { key: 'tel', Icon: CallRounded, label: c.phoneLabel, href: c.phoneHref },
    c.whatsappHref && { key: 'wa', Icon: WhatsApp, label: 'WhatsApp', href: c.whatsappHref, ext: true },
    c.emailHref && { key: 'mail', Icon: MailOutlineRounded, label: c.email, href: c.emailHref },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <Box component="ul" aria-label="Contato" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {links.map(({ key, Icon, label, href, ext }) => (
        <Box component="li" key={key} sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Button
            href={href}
            {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            startIcon={<Icon sx={{ fontSize: 18 }} />}
            sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)', maxWidth: '100%', '& .MuiButton-startIcon': { color: 'var(--color-primary-dark)' } }}
          >
            <Box component="span" sx={ONE_LINE}>{label}</Box>
            {ext && <Box component="span" sx={SR_ONLY}> (abre em outra aba)</Box>}
          </Button>
        </Box>
      ))}
    </Box>
  );
}

function Completeness({ detail, a }) {
  if (!detail.profileCompletenessLabel) return null;
  return (
    <Box sx={{ p: 1.75, borderRadius: 'var(--radius-sm)', border: '1px dashed', borderColor: 'var(--color-primary-light)', bgcolor: 'var(--color-primary-soft)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: 'var(--color-primary-dark)' }}>
        <AutoAwesomeRounded sx={{ fontSize: 20 }} aria-hidden="true" />
        <Typography variant="subtitle2" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{detail.profileCompletenessLabel}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
        Complete quando quiser: cada item abre a edição já no campo certo.
      </Typography>
      {(detail.missingProfileFields || []).length > 0 && (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {detail.missingProfileFields.map((m) => (
            <Box component="li" key={m.key}>
              <Button onClick={() => a.onCompleteProfile?.(detail.id, m.key)} startIcon={<AddRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
                {m.label}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** Bio e descrição: a voz da pessoa. A bio ganha as aspas; a descrição, o tom de nota. */
function About({ detail }) {
  if (!detail.bio && !detail.description) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {detail.bio && (
        <Box component="blockquote" sx={{ m: 0, pl: 1.5, borderLeft: '3px solid', borderColor: 'var(--color-primary-light)', display: 'flex', gap: 0.75 }}>
          <FormatQuoteRounded sx={{ fontSize: 18, color: 'var(--color-primary-dark)', flexShrink: 0, transform: 'scaleX(-1)' }} aria-hidden="true" />
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', textWrap: 'pretty', ...BREAK }}>{detail.bio}</Typography>
        </Box>
      )}
      {detail.description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty', ...BREAK }}>{detail.description}</Typography>}
    </Box>
  );
}

function DetailHero({ detail, a, headRef }) {
  const card = detail.card || {};
  const r = card.readiness || {};
  const own = card.ownership || {};
  const peso = pesoDe(r);
  const issues = r.issues || [];
  const recebido = own.kind === 'received';
  // À direita vai o que pede ação (pendências e o convite para completar o perfil). Sem nada disso, o contato e a
  // bio ocupam esse lado — o veredito calmo no topo já diz que está tudo em ordem.
  const pendente = issues.length > 0 || !!detail.profileCompletenessLabel;
  const espera = aguardando(card.activity);
  const contato = (
    <>
      <About detail={detail} />
      <ContactLinks contact={card.contact} />
    </>
  );
  return (
    <Box sx={{ ...CARD_SX, overflow: 'hidden', containerType: 'inline-size', containerName: 'ficha', gridArea: 'ficha', minWidth: 0 }}>
      {peso === 'alert'
        ? <AlertBand r={r} size="lg" />
        : <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}><VerdictLine r={r} size="lg" /></Box>}
      {/* Três áreas: quem é, o lado (pendências — ou o contato, se nada pende) e as ações. Estreito: uma embaixo da
          outra, com as ações no fim; largo: o lado ocupa a coluna da direita inteira. */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 }, display: 'grid', columnGap: 4, rowGap: 2.5, alignItems: 'start',
          gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateAreas: '"quem" "lado" "acoes"',
          '@container ficha (min-width: 820px)': {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)', gridTemplateRows: 'auto 1fr',
            gridTemplateAreas: '"quem lado" "acoes lado"',
          },
        }}
      >
        <Box sx={{ gridArea: 'quem', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar photoUrl={card.photoUrl} initials={card.initials} size={96} />
              {own.canChangePhoto && (
                <Tooltip title="Trocar a foto">
                  <IconButton
                    aria-label={`Trocar a foto de ${card.name}`}
                    onClick={() => a.onChangePhoto?.(card.id)}
                    sx={{
                      position: 'absolute', right: -6, bottom: -6, width: 40, height: 40, bgcolor: 'var(--surface)',
                      border: '1px solid', borderColor: 'var(--divider)', color: 'var(--color-primary-dark)', boxShadow: 'var(--shadow-card)',
                      '&:hover': { bgcolor: 'var(--color-primary-soft)', borderColor: 'var(--color-primary)' }, ...FOCUS,
                    }}
                  >
                    <PhotoCameraRounded sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <PersonName card={card} variant="h5" component="h2" id="pp-aberto-titulo" headRef={headRef} focusable />
              {card.specialty && <Typography variant="body1" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{card.specialty}</Typography>}
              {card.registration && (
                <Box sx={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', maxWidth: '100%' }}>
                  <BadgeRounded sx={{ fontSize: 15, color: 'var(--text-secondary)' }} aria-hidden="true" />
                  <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', ...BREAK }}>{card.registration}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 }}>
                {card.sinceLabel && <ContextLine icon={AccessTimeRounded}>{card.sinceLabel}</ContextLine>}
                {detail.updatedLabel && <ContextLine icon={UpdateRounded}>{detail.updatedLabel}</ContextLine>}
              </Box>
            </Box>
          </Box>
          <AbsenceLine absence={card.absence} />
          {recebido && <ReceivedTag own={own} />}
          {pendente && contato}
        </Box>

        <Box sx={{ gridArea: 'lado', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pendente ? (
            <>
              <IssueList issues={issues} personId={card.id} scope="pessoa" a={a} title={tituloPendencias(r)} />
              <Completeness detail={detail} a={a} />
            </>
          ) : contato}
        </Box>

        <Box sx={{ gridArea: 'acoes', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {espera && (
            <Box sx={{ flexBasis: '100%', display: 'flex' }}>
              <AwaitingButton label={espera} personId={card.id} a={a} />
            </Box>
          )}
          {own.canBook && r.isBookable && (
            <Button onClick={() => a.onBook?.(card.id)} disableElevation startIcon={<EventAvailableRounded />} sx={PRIMARY_BTN_SX}>
              Agendar
            </Button>
          )}
          {own.canEdit && (
            <Button onClick={() => a.onEdit?.(card.id)} startIcon={<EditRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
              Editar
            </Button>
          )}
          {own.canShare && (
            <Button onClick={() => a.onShare?.(card.id)} startIcon={<ShareRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
              Compartilhar
            </Button>
          )}
          {own.canDelete && (
            <Tooltip title="Excluir">
              <IconButton aria-label={`Excluir ${card.name}`} onClick={() => a.onDelete?.(card.id)} sx={DANGER_ICON_BTN_SX}>
                <DeleteOutlineRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {own.readOnlyReason && !recebido && <Box sx={{ flexBasis: '100%' }}><LockLine>{own.readOnlyReason}</LockLine></Box>}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Onde atende ────────────────────────────────────────────────────────── */

function ScheduleRow({ s, a }) {
  const mode = s.isByChoice ? 'waiting' : s.status === 'success' ? 'live' : 'alert';
  const tone = mode === 'live' ? 'success' : mode === 'waiting' ? 'neutral' : (s.status === 'warning' ? 'warning' : 'error');
  const t = tint(tone);
  const Icon = mode === 'live' ? CheckCircleRounded : mode === 'waiting' ? PauseCircleRounded : EventBusyRounded;
  return (
    <Box component="li">
      <ButtonBase
        onClick={() => a.onOpenSchedule?.(s.id)}
        aria-label={`Abrir a agenda ${s.name} em Agendas`}
        sx={{
          width: '100%', display: 'flex', alignItems: 'stretch', gap: 1, p: 1.25, textAlign: 'left', justifyContent: 'flex-start',
          borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: mode === 'alert' ? t.border : 'var(--divider)',
          bgcolor: mode === 'alert' ? t.bg : 'transparent',
          transition: 'border-color .15s, background-color .15s',
          '&:hover': { borderColor: 'var(--color-primary-light)', bgcolor: mode === 'alert' ? t.bg : 'var(--bg-page)' }, ...FOCUS,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
            <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{s.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: t.fg, minWidth: 0 }}>
              <Icon sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
              <Typography variant="caption" sx={{ ...HEAD, color: 'inherit' }}>{s.headline}</Typography>
            </Box>
          </Box>
          <WeekStrip days={s.daysActive} mode={mode} tone={tone} />
          {(s.weekLabel || s.capacityLabel || s.activityLabel) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {s.weekLabel && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>{s.weekLabel}</Typography>}
              {s.capacityLabel && <ContextLine icon={GroupsRounded}>{s.capacityLabel}</ContextLine>}
              {s.activityLabel && <ContextLine icon={EventAvailableRounded}>{s.activityLabel}</ContextLine>}
            </Box>
          )}
        </Box>
        <ChevronRightRounded sx={{ color: 'var(--text-secondary)', alignSelf: 'center', flexShrink: 0 }} aria-hidden="true" />
      </ButtonBase>
    </Box>
  );
}

/** A régua de preços de um serviço: uma pastilha por forma de pagamento. Quebra de linha, nunca rolagem lateral. */
function PriceRuler({ s }) {
  const list = s.prices || [];
  if (!list.length) return null;
  return (
    <Box
      component="ul"
      aria-label={`Preço por forma de pagamento: ${s.name}`}
      sx={{ ...LIST_RESET, display: 'grid', gap: 0.75, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 148px), 1fr))' }}
    >
      {list.map((p) => {
        const Icon = payIcon(p.paymentType);
        const quieto = p.isDefault || p.isToBeAgreed;
        return (
          <Box
            component="li"
            key={p.paymentName}
            sx={{
              minWidth: 0, px: 1, py: 0.75, borderRadius: 'var(--radius-sm)', border: '1px solid',
              borderColor: p.isCustom ? 'color-mix(in srgb, var(--color-primary) 35%, transparent)' : 'var(--divider)',
              bgcolor: p.isCustom ? 'var(--color-primary-soft)' : 'transparent',
              display: 'flex', flexDirection: 'column', gap: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, color: 'var(--text-secondary)' }}>
              <Icon sx={{ fontSize: 14, flexShrink: 0 }} aria-hidden="true" />
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK, lineHeight: 1.3 }}>{p.paymentName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  ...NUM, ...BREAK,
                  color: quieto ? 'var(--text-secondary)' : 'var(--text-primary)',
                  fontWeight: quieto ? 'var(--fw-regular)' : 'var(--fw-bold)',
                  fontStyle: p.isToBeAgreed ? 'italic' : 'normal',
                }}
              >
                {p.priceLabel}
              </Typography>
              {p.isCustom && (
                <>
                  <StarRounded sx={{ fontSize: 15, color: 'var(--color-primary-dark)', flexShrink: 0 }} aria-hidden="true" />
                  <Box component="span" sx={SR_ONLY}>, preço próprio</Box>
                </>
              )}
            </Box>
            {p.isCustom && p.placePriceLabel && (
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM, ...BREAK }}>{p.placePriceLabel}</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** Próximos horários livres, sob demanda. O botão continua o MESMO elemento enquanto busca (o foco fica nele);
 *  quando a resposta chega e o botão sai, o foco vai para o primeiro horário (ou para "Tentar de novo", ou para o
 *  aviso) — nunca para a página. Uma região viva anuncia o resultado. */
function NextSlots({ ns, personId, placeId, s, canBook, a }) {
  const st = ns?.state || 'idle';
  const regiaoRef = useRef(null);
  const antes = useRef(st);
  useEffect(() => {
    const prev = antes.current;
    antes.current = st;
    if (prev === st || !['idle', 'loading'].includes(prev) || !['loaded', 'empty', 'error'].includes(st)) return;
    if (!focoPerdido()) return;
    const box = regiaoRef.current;
    if (!box) return;
    // O foco vai para o primeiro botão (um horário, ou "Tentar de novo") — ou para a própria região, no aviso —, e
    // o que rola à vista é a REGIÃO inteira (com a folga do aviso), não só o botão.
    levarFoco(box.querySelector('button') || box, false);
    mostrar(box);
  }, [st]);
  const buscar = () => a.onFindNextSlots?.(personId, placeId, s.serviceId);
  const slots = (ns?.slots || []).slice(0, 3);
  return (
    <Box ref={regiaoRef} tabIndex={-1} sx={{ minWidth: 0, outline: 'none', ...SCROLL_MARGIN }}>
      <Box aria-live="polite" sx={{ display: 'contents' }}>
        {(st === 'idle' || st === 'loading') && (
          <Button
            onClick={st === 'idle' ? buscar : undefined}
            aria-disabled={st === 'loading' || undefined}
            startIcon={st === 'loading'
              ? <CircularProgress size={16} thickness={5} sx={{ color: 'var(--color-primary-dark)' }} />
              : <ScheduleRounded sx={{ fontSize: 18 }} />}
            sx={{ ...QUIET_BTN_SX, color: 'var(--color-primary-dark)', ml: -1.25, ...(st === 'loading' && { cursor: 'progress' }) }}
          >
            {st === 'loading' ? 'Buscando horários livres…' : 'Ver próximos horários'}
          </Button>
        )}
        {st === 'loaded' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="caption" component="p" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>Próximos horários livres</Typography>
            <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {slots.map((sl) => (
                <Box component="li" key={sl.key}>
                  {canBook ? (
                    <Button
                      onClick={() => a.onBook?.(personId, placeId, s.serviceId, sl.key)}
                      startIcon={<EventAvailableRounded sx={{ fontSize: 18 }} />}
                      aria-label={`Agendar ${s.name}: ${sl.label}`}
                      sx={{
                        minHeight: 40, px: 1.5, borderRadius: 999, textTransform: 'none', whiteSpace: 'nowrap',
                        bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)',
                        border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
                        '&:hover': { bgcolor: 'var(--color-primary)', color: 'var(--on-primary)' }, ...FOCUS,
                      }}
                    >
                      {sl.label}
                    </Button>
                  ) : (
                    <Box sx={{ minHeight: 40, px: 1.5, borderRadius: 999, border: '1px solid', borderColor: 'var(--divider)', display: 'inline-flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{sl.label}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {st === 'empty' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: 'var(--text-secondary)', py: 0.5 }}>
            <EventBusyRounded sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
            <Typography variant="body2" sx={{ color: 'inherit', textWrap: 'pretty' }}>{ns.message}</Typography>
          </Box>
        )}
        {st === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: 'var(--tint-warning-fg)', minWidth: 0, flex: '1 1 200px' }}>
              <WarningAmberRounded sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
              <Typography variant="body2" sx={{ color: 'inherit', textWrap: 'pretty' }}>{ns.message}</Typography>
            </Box>
            <Button onClick={buscar} startIcon={<ReplayRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>Tentar de novo</Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ServiceBlock({ s, personId, placeId, own, a }) {
  const tom = pesoServico(s);
  const defeito = ehDefeito(tom);
  const tt = tint(tom);
  const Icon = s.notBookableIsByChoice ? PauseCircleRounded : EventBusyRounded;
  const meta = [s.category, s.durationLabel].filter(Boolean).join(' · ');
  return (
    <Box
      component="li"
      sx={{
        p: 1.5, borderRadius: 'var(--radius-sm)', border: '1px solid', display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0,
        borderColor: defeito ? tt.border : 'var(--divider)', bgcolor: defeito ? tt.bg : 'transparent',
        ...(defeito && { borderLeft: '3px solid', borderLeftColor: tt.main }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
          <Typography variant="body2" component="p" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)' }}>{s.name}</Typography>
          {meta && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>{meta}</Typography>}
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)' }}>Preço padrão</Typography>
          <Typography variant="body2" component="p" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', fontStyle: s.basePriceIsToBeAgreed ? 'italic' : 'normal' }}>{s.priceLabel}</Typography>
        </Box>
      </Box>

      {s.isBookable ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--tint-success-fg)' }}>
          <CheckCircleRounded sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)' }}>Dá para agendar</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: tt.fg }}>
          <Icon sx={{ fontSize: 16, mt: '1px', flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: defeito ? 'var(--fw-bold)' : 'var(--fw-regular)', textWrap: 'pretty' }}>
            <Box component="span" sx={SR_ONLY}>{`${ISSUE_SR[tom] || ''}: `}</Box>
            {s.notBookableReason}
          </Typography>
        </Box>
      )}

      <PriceRuler s={s} />

      {s.customPricesLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--color-primary-dark)', minWidth: 0 }}>
          <StarRounded sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{s.customPricesLabel}</Typography>
        </Box>
      )}

      {/* Rodapé do serviço: os próximos horários (sob demanda) à esquerda, o preço próprio à direita. */}
      {(s.isBookable || own.canEditPrices) && (
        <Box sx={{ pt: 0.5, borderTop: '1px dashed', borderColor: 'var(--divider)', display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
          <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
            {s.isBookable && <NextSlots ns={s.nextSlots} personId={personId} placeId={placeId} s={s} canBook={own.canBook} a={a} />}
          </Box>
          {own.canEditPrices && (
            <Button onClick={() => a.onEditCustomPrices?.(personId, placeId, s.serviceId)} startIcon={<TuneRounded sx={{ fontSize: 18 }} />} aria-label={`Preço próprio: ${s.name}`} sx={{ ...QUIET_BTN_SX, ml: 'auto', mr: -1 }}>
              Preço próprio
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function WorkplaceCard({ w, personId, own, a, delay }) {
  const r = w.readiness || {};
  const peso = pesoDe(r);
  const headingId = `pp-local-${w.placeId}`;
  const fortes = (r.issues || []).filter((i) => !i.isByChoice && i.status !== 'info');
  const primeira = fortes.find((i) => i.fix && i.actionLabel);
  return (
    <Box
      component="article"
      aria-labelledby={headingId}
      sx={{ ...CARD_SX, ...rise(delay), overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column', borderColor: peso === 'alert' ? tint(tomAlerta(r)).border : 'var(--divider)' }}
    >
      {peso === 'alert' ? (
        <AlertBand r={r}>
          {primeira && (
            <Box sx={{ pl: { xs: 0, sm: 6.5 } }}>
              <Button onClick={() => a.onFix?.(personId, primeira.fix)} disableElevation endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />} sx={{ ...PRIMARY_BTN_SX, minHeight: 40, whiteSpace: 'normal', textAlign: 'left' }}>
                {primeira.actionLabel}
              </Button>
            </Box>
          )}
        </AlertBand>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 2.25 }, pt: { xs: 2, sm: 2.25 } }}><VerdictLine r={r} /></Box>
      )}
      <Box sx={{ p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          <Box aria-hidden="true" sx={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <StorefrontRounded sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography id={headingId} variant="subtitle1" component="h4" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', lineHeight: 1.3 }}>{w.placeName}</Typography>
            {w.addressLabel && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', ...BREAK }}>{w.addressLabel}</Typography>}
          </Box>
          {w.mapsUrl && (
            <Tooltip title="Ver no mapa">
              <IconButton component="a" href={w.mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={`Ver ${w.placeName} no mapa (abre em outra aba)`} sx={{ ...ICON_BTN_SX, mt: -0.5 }}>
                <MapRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {(w.schedules || []).length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="overline" component="h5" sx={OVERLINE}>A semana</Typography>
            <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {w.schedules.map((s) => <ScheduleRow key={s.id} s={s} a={a} />)}
            </Box>
          </Box>
        )}

        {(w.services || []).length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="overline" component="h5" sx={OVERLINE}>O que faz aqui</Typography>
            <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {w.services.map((s) => <ServiceBlock key={s.serviceId} s={s} personId={personId} placeId={w.placeId} own={own} a={a} />)}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WorkplacesSection({ detail, a }) {
  const card = detail.card || {};
  const own = card.ownership || {};
  const list = detail.workplaces || [];
  const issues = card.readiness?.issues || [];
  const semLugar = issues.find((i) => i.fix && i.fix.type === 'linkPlace');
  return (
    <Box component="section" aria-labelledby="pp-sec-locais" sx={{ gridArea: 'locais', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <PlaceRounded sx={{ fontSize: 22, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
          <Box sx={{ minWidth: 0 }}>
            <Typography id="pp-sec-locais" variant="h6" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)' }}>Onde atende</Typography>
            {card.placesSummary?.label && list.length > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {[card.placesSummary.label, card.servicesSummary?.label].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>
        {!own.canEditPrices && own.readOnlyReason && <Box sx={{ maxWidth: 360 }}><LockLine>{own.readOnlyReason}</LockLine></Box>}
      </Box>
      {list.length === 0 ? (
        <EmptyNote
          icon={LocationOffRounded}
          tone="error"
          action={semLugar ? (
            <Button onClick={() => a.onFix?.(card.id, semLugar.fix)} disableElevation endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />} sx={{ ...PRIMARY_BTN_SX, minHeight: 40, whiteSpace: 'normal' }}>
              {semLugar.actionLabel}
            </Button>
          ) : null}
        >
          {semLugar ? semLugar.label : card.placesSummary?.label}
        </EmptyNote>
      ) : (
        <Box
          sx={{
            display: 'grid', gap: 'var(--space-2)', alignItems: 'start', gridTemplateColumns: 'minmax(0, 1fr)',
            containerType: 'inline-size', containerName: 'locais',
          }}
        >
          <Box
            sx={{
              display: 'grid', gap: 'var(--space-2)', alignItems: 'start', gridTemplateColumns: 'minmax(0, 1fr)',
              '@container locais (min-width: 940px)': list.length > 1 ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : {},
            }}
          >
            {list.map((w, i) => <WorkplaceCard key={w.placeId} w={w} personId={card.id} own={own} a={a} delay={60 + i * 60} />)}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/* ── O que a pessoa vive: movimento, compartilhamento, páginas, ausências ── */
function ActivitySection({ detail, a }) {
  const stats = detail.activityStats || [];
  const act = detail.card?.activity || {};
  // "Aguardando confirmação" com algo esperando vira um BOTÃO (leva a Agendamentos), na ordem do vm.
  const esperaAtiva = (s) => s.key === 'awaitingConfirmation' && act.awaitingConfirmationCount > 0;
  const numeros = stats.filter((s) => !esperaAtiva(s));
  const espera = stats.filter(esperaAtiva);
  const vazio = stats.length === 0;
  return (
    <Section id="pp-sec-movimento" icon={InsightsRounded} title="Movimento">
      {act.nextAppointmentLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--color-primary)', flexShrink: 0,
              '@keyframes ppPulso': { '0%': { boxShadow: '0 0 0 0 var(--color-primary-light)' }, '70%, 100%': { boxShadow: '0 0 0 8px transparent' } },
              animation: 'ppPulso 2.4s ease-out infinite', [REDUCED]: { animation: 'none' },
            }}
          />
          <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{act.nextAppointmentLabel}</Typography>
        </Box>
      )}
      {!act.nextAppointmentLabel && act.lastAppointmentLabel && <ContextLine icon={AccessTimeRounded}>{act.lastAppointmentLabel}</ContextLine>}
      {vazio ? (
        <EmptyNote icon={AutoAwesomeRounded}>{detail.activityEmptyLabel || act.emptyLabel}</EmptyNote>
      ) : (
        <>
          {numeros.length > 0 && (
            <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))' }}>
              {numeros.map((s) => (
                <Box key={s.key} sx={{ p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'flex-end', gap: 0.25, minWidth: 0 }}>
                  <Box component="dt" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty', display: 'block' }}>{s.label}</Typography>
                    {s.hint && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', textWrap: 'pretty', display: 'block' }}>{s.hint}</Typography>}
                  </Box>
                  <Box component="dd" sx={{ m: 0 }}>
                    <Typography variant="h5" component="span" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.1 }}>{s.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {espera.map((s) => (
            <ButtonBase
              key={s.key}
              onClick={() => a.onOpenAwaitingConfirmation?.(detail.id)}
              sx={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.25, p: 1.25, textAlign: 'left',
                borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
                border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
                transition: 'border-color .15s', '&:hover': { borderColor: 'var(--color-primary)' },
                '&:hover .pp-seta': { transform: 'translateX(3px)' }, ...FOCUS,
              }}
            >
              <HourglassTopRounded sx={{ fontSize: 22, flexShrink: 0 }} aria-hidden="true" />
              <Typography variant="h5" component="span" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.1 }}>{s.value}</Typography>
              <Box component="span" sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{s.label}</Typography>
                {s.hint && <Typography variant="caption" component="span" sx={{ color: 'var(--color-primary-dark)', textWrap: 'pretty' }}>{s.hint}</Typography>}
              </Box>
              <ArrowForwardRounded className="pp-seta" sx={{ fontSize: 18, flexShrink: 0, transition: 'transform .2s', [REDUCED]: { transition: 'none' } }} aria-hidden="true" />
            </ButtonBase>
          ))}
          {act.onlineShareLabel && !numeros.some((s) => s.key === 'online') && <ContextLine icon={LanguageRounded}>{act.onlineShareLabel}</ContextLine>}
        </>
      )}
    </Section>
  );
}

function SharingSection({ detail, areas, a }) {
  const card = detail.card || {};
  const own = card.ownership || {};
  const recebido = own.kind === 'received';
  const rows = detail.sharedWith || [];
  if (recebido) {
    return (
      <Section id="pp-sec-acesso" icon={HandshakeRounded} title="De outra conta" subtitle={own.accessLabel}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box aria-hidden="true" sx={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--tint-info-bg)', color: 'var(--tint-info-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <HandshakeRounded sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{own.ownerName}</Typography>
        </Box>
        <PermMeter areas={areas} levels={own.permissionLevels} label="O que você pode fazer" />
      </Section>
    );
  }
  return (
    <Section
      id="pp-sec-acesso"
      icon={ShareRounded}
      title="Quem mais vê"
      subtitle={card.sharing?.label}
      action={own.canShare ? <Button onClick={() => a.onShare?.(card.id)} startIcon={<ShareRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>Compartilhar</Button> : null}
    >
      {rows.length === 0 ? (
        <EmptyNote icon={LockRounded}>Só a sua conta tem acesso.</EmptyNote>
      ) : (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((x) => (
            <Box component="li" key={x.email} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.25, borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)', minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{x.name}</Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', ...BREAK }}>{x.email}</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>{x.accessLabel}</Typography>
              <PermMeter areas={areas} levels={x.permissionLevels} label={`Acesso de ${x.name}`} />
              {x.sinceLabel && <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{x.sinceLabel}</Typography>}
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
}

function PagesSection({ detail, a }) {
  const pages = detail.pages || [];
  const pres = detail.card?.presence || {};
  return (
    <Section id="pp-sec-paginas" icon={PublicRounded} title="Onde aparece" subtitle={pres.label}>
      {pages.length === 0 ? (
        <EmptyNote icon={PublicRounded}>Ainda não aparece em nenhuma página no ar.</EmptyNote>
      ) : (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {pages.map((p) => (
            <Box component="li" key={p.id}>
              <ButtonBase
                onClick={() => a.onOpenPage?.(p.url)}
                aria-label={`Abrir a página ${p.title}`}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1, p: 1.25, textAlign: 'left', justifyContent: 'flex-start',
                  borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)',
                  '&:hover': { borderColor: 'var(--color-primary-light)', bgcolor: 'var(--bg-page)' }, ...FOCUS,
                }}
              >
                <LanguageRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)', flexShrink: 0 }} aria-hidden="true" />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', display: 'block', ...BREAK }}>{p.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{[p.typeLabel, p.scopeLabel].filter(Boolean).join(' · ')}</Typography>
                </Box>
                <OpenInNewRounded sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden="true" />
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
}

/** Ausências como algo que se vê no tempo: um marco por período, o de agora em destaque. */
function AbsencesSection({ detail }) {
  const list = detail.absences || [];
  if (!list.length) return null;
  return (
    <Section id="pp-sec-ausencias" icon={BeachAccessRounded} title="Ausências">
      <Box component="ul" sx={{ ...LIST_RESET, position: 'relative', display: 'flex', flexDirection: 'column', gap: 1, pl: 2.5, '&::before': { content: '""', position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, bgcolor: 'var(--divider)', borderRadius: 2 } }}>
        {list.map((x) => {
          const t = tint(x.isNow ? 'warning' : 'info');
          return (
            <Box component="li" key={x.id} sx={{ position: 'relative', p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: t.bg, border: '1px solid', borderColor: t.border }}>
              <Box aria-hidden="true" sx={{ position: 'absolute', left: -22, top: 14, width: 14, height: 14, borderRadius: '50%', bgcolor: 'var(--surface)', border: '3px solid', borderColor: t.main }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ ...HEAD, ...NUM, color: t.fg }}>{x.label}</Typography>
                {x.isNow && (
                  <Box sx={{ px: 0.75, borderRadius: 999, border: '1px solid', borderColor: t.border, bgcolor: 'var(--surface)' }}>
                    <Typography variant="caption" sx={{ ...HEAD, color: t.fg }}>Agora</Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--text-primary)', display: 'block' }}>{[x.reason, x.placesLabel].filter(Boolean).join(' · ')}</Typography>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

function PersonDetail({ items, detail, labels, areas, a, headRef, rootRef, single }) {
  const recebido = detail.card?.ownership?.kind === 'received';
  return (
    <Box ref={rootRef} component="section" aria-labelledby="pp-aberto-titulo" sx={{ ...rise(0), ...SCROLL_MARGIN, containerType: 'inline-size', containerName: 'aberta' }}>
      {!single && <DetailNav items={items} selectedId={detail.id} labels={labels} a={a} />}
      <Box
        sx={{
          display: 'grid', gap: 'var(--space-2)', alignItems: 'start',
          gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateAreas: '"ficha" "locais" "lado"',
          '@container aberta (min-width: 1000px)': {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 340px)', gridTemplateAreas: '"ficha ficha" "locais lado"',
          },
          '@container aberta (min-width: 1480px)': {
            gridTemplateColumns: 'minmax(0, 400px) minmax(0, 1fr) minmax(0, 340px)', gridTemplateAreas: '"ficha locais lado"',
          },
        }}
      >
        <DetailHero detail={detail} a={a} headRef={headRef} />
        <WorkplacesSection detail={detail} a={a} />
        {/* Ao lado (≥ 1000 px) é uma coluna; abaixo disso as seções se repartem em até 3 colunas. */}
        <Box sx={{ gridArea: 'lado', display: 'grid', gap: 'var(--space-2)', alignItems: 'start', minWidth: 0, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
          <ActivitySection detail={detail} a={a} />
          <SharingSection detail={detail} areas={areas} a={a} />
          {!recebido && <PagesSection detail={detail} a={a} />}
          <AbsencesSection detail={detail} />
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Topo, estados
   ═══════════════════════════════════════════════════════════════════════════ */

/** O botão de adicionar (o texto vem em labels.actionLabel). Com o plano apertado ou cheio, a explicação vem ANTES
 *  do clique, colada no botão. */
function NewPerson({ labels, plan, a }) {
  const bloqueado = !!plan && !plan.canCreate;
  const t = tint(plan?.status);
  const pct = plan && plan.limit ? Math.min(100, Math.round((plan.used / plan.limit) * 100)) : 0;
  const avisa = !!plan && (plan.status === 'warning' || plan.status === 'error');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 0.75, width: { xs: '100%', sm: 'auto' }, maxWidth: { sm: 380 } }}>
      <Button
        onClick={() => a.onNew?.()}
        disableElevation
        startIcon={bloqueado ? <WorkspacePremiumRounded /> : <AddRounded />}
        aria-describedby={plan ? 'pp-plano' : undefined}
        sx={bloqueado
          ? { ...OUTLINE_BTN_SX, minHeight: 42, px: 2.25, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }
          : PRIMARY_BTN_SX}
      >
        {labels.actionLabel}
      </Button>
      {plan && (
        <Box id="pp-plano" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: { sm: 'flex-end' }, textAlign: { sm: 'right' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box aria-hidden="true" sx={{ width: 72, height: 6, borderRadius: 3, bgcolor: 'var(--divider)', overflow: 'hidden', flexShrink: 0 }}>
              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: t.main }} />
            </Box>
            <Typography variant="caption" sx={{ ...HEAD, color: t.fg }}>{plan.label}</Typography>
          </Box>
          {avisa && plan.message && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{plan.message}</Typography>}
        </Box>
      )}
    </Box>
  );
}

function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.5, textWrap: 'pretty' }}>{title}</Typography>
      {description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 440, textWrap: 'pretty' }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Ilustração do vazio: os exemplos do nicho virando uma equipe que recebe agendamento. Chega uma vez e fica. */
function TeamSketch({ examples }) {
  const nomes = (examples || []).slice(0, 3);
  const lista = nomes.length ? nomes : ['', '', ''];
  return (
    <Box aria-hidden="true" sx={{ width: '100%', maxWidth: 340, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {lista.map((nome, i) => (
        <Box
          key={`${nome}-${i}`}
          sx={{
            bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-card)',
            boxShadow: i === 0 ? 'var(--shadow-hover)' : 'var(--shadow-card)', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
            ml: i * 2, mr: (2 - i) * 2,
            '@keyframes ppChega': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
            animation: 'ppChega .6s cubic-bezier(.2,.7,.3,1) both', animationDelay: `${150 + i * 160}ms`,
            [REDUCED]: { animation: 'none' },
          }}
        >
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PersonRounded sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {nome ? (
              <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-primary)', ...ONE_LINE }}>{nome}</Typography>
            ) : (
              <Box sx={{ height: 8, width: '58%', borderRadius: 4, bgcolor: 'var(--divider)' }} />
            )}
            <Box sx={{ display: 'flex', gap: '3px' }}>
              {DAY_INITIAL.map((_, d) => (
                <Box key={d} sx={{ flex: 1, height: 6, borderRadius: 2, bgcolor: (d + i) % 7 < 5 ? 'var(--color-primary-soft)' : 'var(--bg-page)' }} />
              ))}
            </Box>
          </Box>
          <CheckCircleRounded
            sx={{
              fontSize: 20, color: 'var(--color-success)', flexShrink: 0,
              '@keyframes ppOk': { from: { opacity: 0, transform: 'scale(.6)' }, to: { opacity: 1, transform: 'none' } },
              animation: 'ppOk .35s ease-out both', animationDelay: `${700 + i * 220}ms`,
              [REDUCED]: { animation: 'none' },
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function EmptyHero({ empty, a }) {
  const ex = empty.examples || [];
  return (
    <Box sx={{ ...CARD_SX, ...rise(0), containerType: 'inline-size', overflow: 'hidden' }}>
      <Box
        sx={{
          p: { xs: 2.5, sm: 4 }, display: 'grid', gap: 4, alignItems: 'center', gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 760px)': { gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 6, p: 5 },
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 560 }}>
          <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{empty.title}</Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{empty.body}</Typography>
          {ex.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', mb: 0.75 }}>Por exemplo:</Typography>
              <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {ex.map((e) => (
                  <Box component="li" key={e} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', maxWidth: '100%' }}>
                    <PersonRounded sx={{ fontSize: 16, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
                    <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', ...BREAK }}>{e}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {empty.actionLabel && (
            <Box sx={{ mt: 1.5 }}>
              <Button onClick={() => a.onNew?.()} disableElevation startIcon={<AddRounded />} sx={CTA_BTN_SX}>{empty.actionLabel}</Button>
            </Box>
          )}
        </Box>
        <TeamSketch examples={ex} />
      </Box>
    </Box>
  );
}

const SK = { bgcolor: 'var(--bg-page)' };

/** Skeleton com a FORMA do que vem: faixa de resumo + régua, filtros e fichas (rosto, nome, linhas). */
function LoadingSkeleton() {
  return (
    <Box role="status" aria-busy="true">
      <Box component="span" sx={SR_ONLY}>Carregando…</Box>
      <Box aria-hidden="true">
        <Box sx={{ ...CARD_SX, p: 2.25, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Skeleton variant="rounded" width={28} height={28} sx={{ ...SK, borderRadius: '6px' }} />
            <Skeleton variant="text" width={150} height={30} sx={SK} />
            {[170, 190].map((w, i) => <Skeleton key={i} variant="rounded" width={w} height={44} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />)}
            <Box sx={{ flex: '1 1 160px', display: 'flex', gap: '3px' }}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rounded" height={8} sx={{ ...SK, flex: 1, maxWidth: 56, borderRadius: 3 }} />)}
            </Box>
          </Box>
          <Skeleton variant="text" width="min(520px, 80%)" sx={SK} />
        </Box>
        <Box sx={{ ...CARD_SX, p: 1.75, mb: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
          <Skeleton variant="rounded" height={42} sx={{ ...SK, flex: '2 1 260px', borderRadius: 'var(--radius-sm)' }} />
          <Skeleton variant="rounded" height={42} sx={{ ...SK, flex: '1 1 190px', borderRadius: 'var(--radius-sm)' }} />
          <Skeleton variant="rounded" width={92} height={42} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
        </Box>
        <Box sx={CARDS_GRID}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} sx={{ ...CARD_SX, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.5, '& .MuiSkeleton-root': { animationDelay: `${i * 140}ms` } }}>
              <Skeleton variant="text" width="44%" sx={SK} />
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Skeleton variant="circular" width={56} height={56} sx={SK} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={`${58 + (i % 2) * 14}%`} height={26} sx={SK} />
                  <Skeleton variant="text" width="40%" sx={SK} />
                </Box>
              </Box>
              <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Skeleton variant="text" width="52%" sx={SK} />
                <Skeleton variant="text" width="34%" sx={SK} />
              </Box>
              <Skeleton variant="text" width="62%" sx={SK} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function ProfessionalsView({ vm, actions }) {
  const a = actions || {};
  const labels = vm.labels || {};
  const groups = vm.groups || [];
  const items = groups.flatMap((g) => g.items || []);
  const f = vm.filters || {};
  const st = vm.viewState;
  const detail = vm.detail && vm.selectedId ? vm.detail : null;
  const single = st === 'ready' && items.length === 1 && !f.isFiltered;
  const firstAlert = items.find((it) => pesoDe(it.readiness) === 'alert');

  const [flash, setFlash] = useState(null);   // UI: cartão realçado depois de "ir até o primeiro"
  const timers = useRef([]);
  const pageTitleRef = useRef(null);
  const detailHeadRef = useRef(null);
  const detailRootRef = useRef(null);
  const openRefs = useRef({});   // id → botão que abre a pessoa (para onde o foco volta)
  const cardRefs = useRef({});   // id → o cartão (destino do "ir até o primeiro")

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Abriu (ou trocou de) pessoa → foco no nome, e o começo dela à vista sem pular a página. Fechou → o foco volta
  // para quem abriu. No primeiro desenho (a pessoa única já chega aberta) nada se mexe.
  const abertoAntes = useRef(detail ? detail.id : null);
  useEffect(() => {
    const antes = abertoAntes.current;
    const agora = detail ? detail.id : null;
    abertoAntes.current = agora;
    if (antes === agora) return;
    if (agora) {
      levarFoco(detailHeadRef.current, false);
      mostrarInicio(detailRootRef.current);
      return;
    }
    if (focoPerdido()) levarFoco(openRefs.current[antes] || cardRefs.current[antes] || pageTitleRef.current);
  }, [detail]);

  const jump = (id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    levarFoco(el);
    setFlash(id);
    timers.current.push(setTimeout(() => setFlash((x) => (x === id ? null : x)), 1800));
  };

  const regOpen = (id) => (el) => { openRefs.current[id] = el; };
  const regCard = (id) => (el) => { cardRefs.current[id] = el; };

  let body = null;
  let showSummary = false;
  let showFilters = false;
  if (st === 'loading') {
    body = <LoadingSkeleton />;
  } else if (st === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title={vm.errorMessage || 'Não foi possível carregar agora.'}
        action={<Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>Tentar novamente</Button>}
      />
    );
  } else if (st === 'empty' && vm.emptyState) {
    body = <EmptyHero empty={vm.emptyState} a={a} />;
  } else if (detail) {
    body = <PersonDetail items={items} detail={detail} labels={labels} areas={vm.permissionAreas} a={a} headRef={detailHeadRef} rootRef={detailRootRef} single={single} />;
  } else if (items.length === 0 && !groups.some((g) => g.emptyLabel)) {
    const e = vm.emptyState;
    showSummary = true;
    showFilters = true;
    body = (
      <StateBlock
        icon={<SearchOffRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title={e?.title || 'Nada encontrado'}
        description={e?.body}
        action={e?.actionLabel ? (
          <Button onClick={() => a.onClearFilters?.()} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42 }}>{e.actionLabel}</Button>
        ) : null}
      />
    );
  } else {
    showSummary = true;
    showFilters = true;
    const visiveis = groups.filter((g) => (g.items || []).length > 0 || g.emptyLabel);
    const comCabecalho = visiveis.length > 1 || visiveis.some((g) => g.key === 'received');
    let offset = 0;
    body = (
      <Box>
        {visiveis.map((g) => {
          const el = (
            <GroupSection key={g.key} group={g} showHeader={comCabecalho} a={a} regCard={regCard} regOpen={regOpen} flash={flash} offset={offset} />
          );
          offset += (g.items || []).length;
          return el;
        })}
      </Box>
    );
  }

  const showNew = st !== 'empty' && st !== 'error';

  return (
    // O fim da página tem a folga do aviso do container: o último botão sempre pode subir acima dele.
    <Box sx={{ color: 'var(--text-primary)', pb: FOLGA }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
            <Typography ref={pageTitleRef} tabIndex={-1} variant="h1" component="h1" sx={{ ...FOCUS_TITLE_SX, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {labels.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, maxWidth: 640 }}>{labels.subtitle}</Typography>
        </Box>
        {showNew && <NewPerson labels={labels} plan={vm.planUsage} a={a} />}
      </Box>

      {showSummary && <SummaryBand summary={vm.summary} items={items} onJump={jump} firstAlert={firstAlert} />}
      {showFilters && <FiltersBar filters={f} labels={labels} a={a} />}
      {body}
    </Box>
  );
}
